import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Autocomplete,
  alpha,
  useTheme,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  LinkIcon,
  Users,
  Building2,
} from "lucide-react";
import {
  fetchMarketingOrg,
  createMarketingOrg,
  updateMarketingOrg,
  linkContactToOrg,
  unlinkContactFromOrg,
} from "../../../../../redux/actions/facturationAction";
import { enqueueSnackbar } from "notistack";
import { backendClient } from "../../../../../api/backend";

const ClientDetail = ({ orgId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentMarketingOrg, loading } = useSelector((state) => state.facturation);
  const isEdit = Boolean(orgId);

  const [form, setForm] = useState({
    name: "",
    ice: "",
    address: "",
    city: "",
    country: "Maroc",
    phone: "",
    website: "",
    notes: "",
  });

  const [saving, setSaving] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [linkingContact, setLinkingContact] = useState(null);

  // Load data
  useEffect(() => {
    if (isEdit) {
      dispatch(fetchMarketingOrg(orgId));
    }
    // Load all marketing contacts for linking
    backendClient.getMarketingContacts?.({ page_size: 100 })
      ?.then?.((data) => setAllContacts(data.items || []))
      ?.catch?.(() => {});
  }, [dispatch, orgId, isEdit]);

  // Fill form when editing
  useEffect(() => {
    if (isEdit && currentMarketingOrg && currentMarketingOrg.id === orgId) {
      setForm({
        name: currentMarketingOrg.name || "",
        ice: currentMarketingOrg.ice || "",
        address: currentMarketingOrg.address || "",
        city: currentMarketingOrg.city || "",
        country: currentMarketingOrg.country || "Maroc",
        phone: currentMarketingOrg.phone || "",
        website: currentMarketingOrg.website || "",
        notes: currentMarketingOrg.notes || "",
      });
    }
  }, [isEdit, currentMarketingOrg, orgId]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      enqueueSnackbar("Nom de l'organisation requis", { variant: "warning" });
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await dispatch(updateMarketingOrg(orgId, form));
        enqueueSnackbar("Organisation mise à jour", { variant: "success" });
      } else {
        await dispatch(createMarketingOrg(form));
        enqueueSnackbar("Organisation créée", { variant: "success" });
        navigate("/admin/facturation/clients");
      }
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleLinkContact = async (_, contact) => {
    if (!contact || !isEdit) return;
    try {
      await dispatch(linkContactToOrg(orgId, contact.id));
      enqueueSnackbar("Contact lié", { variant: "success" });
      setLinkingContact(null);
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    }
  };

  const handleUnlinkContact = async (contactId) => {
    try {
      await dispatch(unlinkContactFromOrg(orgId, contactId));
      enqueueSnackbar("Contact délié", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    }
  };

  const orgContacts = currentMarketingOrg?.contacts || [];
  const linkedContactIds = new Set(orgContacts.map((c) => c.id));
  const availableContacts = allContacts.filter((c) => !linkedContactIds.has(c.id));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton size="small" onClick={() => navigate("/admin/facturation/clients")}>
            <ArrowLeft size={18} />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isEdit ? currentMarketingOrg?.name || "Organisation" : "Nouvelle organisation"}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={14} />}
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {isEdit ? "Mettre à jour" : "Créer"}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Organization info */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem", color: theme.palette.text.secondary }}>
              Informations de l'organisation
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth size="small" label="Nom *" value={form.name} onChange={handleChange("name")} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="ICE" value={form.ice} onChange={handleChange("ice")} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Adresse" value={form.address} onChange={handleChange("address")} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Ville" value={form.city} onChange={handleChange("city")} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Pays" value={form.country} onChange={handleChange("country")} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Téléphone" value={form.phone} onChange={handleChange("phone")} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Site web" value={form.website} onChange={handleChange("website")} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth multiline rows={2} size="small" label="Notes internes"
                  value={form.notes} onChange={handleChange("notes")}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Contacts */}
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem", color: theme.palette.text.secondary }}>
                Contacts ({orgContacts.length})
              </Typography>
            </Box>

            {isEdit ? (
              <>
                {/* Link existing contact */}
                <Autocomplete
                  options={availableContacts}
                  getOptionLabel={(c) => `${c.first_name || ""} ${c.last_name || ""} — ${c.email}`}
                  value={linkingContact}
                  onChange={handleLinkContact}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Lier un contact existant..." size="small" />
                  )}
                  size="small"
                  sx={{ mb: 2 }}
                />

                {/* Contact list */}
                {orgContacts.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Users size={32} strokeWidth={1} color={theme.palette.text.disabled} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Aucun contact lié
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {orgContacts.map((contact) => (
                      <Box
                        key={contact.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: 1.5,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {[contact.first_name, contact.last_name].filter(Boolean).join(" ") || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {contact.email}
                          </Typography>
                          {contact.phone && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {contact.phone}
                            </Typography>
                          )}
                        </Box>
                        <IconButton size="small" onClick={() => handleUnlinkContact(contact.id)}>
                          <Trash2 size={14} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
                Créez d'abord l'organisation, puis ajoutez des contacts.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientDetail;
